import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { ResponsiveContainer, ScatterChart as RechartsScatterChart, Scatter, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import { fontSizes } from '../../theme/theme';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';

interface ScatterChartData {
  [key: string]: any;
}

interface QuadrantConfig {
  name: string;
  label: string;
  color: string;
  count?: number;
  percentage?: number;
}

interface ReferenceLineConfig {
  axis: 'x' | 'y';
  value: number;
  color?: string;
  label?: string;
}

interface ScatterChartProps extends ChartHeaderProps {
  data: ScatterChartData[];
  height?: number;
  graphHeight?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  xAxisKey?: string;
  yAxisKey?: string;
  quadrantKey?: string;
  tooltipKey?: string;
  xAxisUnit?: string;
  yAxisUnit?: string;
  showZones?: boolean;
  quadrants?: QuadrantConfig[];
  referenceLines?: ReferenceLineConfig[];
  idealZoneThreshold?: { minQuality: number; maxDuration?: number };
  problemZoneThreshold?: { maxQuality: number; minDuration?: number };
  onDetailsClick?: (data?: any) => void;
}

const ScatterChart: React.FC<ScatterChartProps> = ({
  title,
  description,
  data,
  details,
  height = 400,
  graphHeight = 400,
  xAxisTitle = 'Duration',
  yAxisTitle = 'Quality',
  xAxisKey = 'duration',
  yAxisKey = 'quality',
  quadrantKey = 'zone',
  tooltipKey = 'tooltip_text',
  xAxisUnit = ' min',
  yAxisUnit = '%',
  showZones = true,
  quadrants,
  referenceLines,
  idealZoneThreshold = { minQuality: 75, maxDuration: 60 },
  problemZoneThreshold = { maxQuality: 25, minDuration: 60 },
  onDetailsClick
}) => {
  // Use quadrants from data details or props, or create default
  const effectiveQuadrants = quadrants || details?.quadrant_summaries?.map((q: any) => ({
    name: q.quadrant.toLowerCase(),
    label: q.quadrant,
    color: q.color,
    count: q.count,
    percentage: q.percentage
  })) || [
      { name: 'ideal', label: 'Ideal', color: '#10B981', count: 0 },
      { name: 'problem', label: 'Problem', color: '#EF4444', count: 0 },
      { name: 'normal', label: 'Normal', color: '#6B7280', count: 0 }
    ];

  // Group data by quadrants
  const quadrantCounts = effectiveQuadrants.reduce((acc: Record<string, number>, q: QuadrantConfig) => {
    acc[q.name] = data.filter(item => item[quadrantKey]?.toLowerCase() === q.name).length;
    return acc;
  }, {} as Record<string, number>);

  const handlePointClick = (data: any) => {
    if (onDetailsClick) {
      onDetailsClick(data);
    }
    console.log('Clicked Scatter Point:');
    console.log(`${xAxisTitle}:`, data[xAxisKey]);
    console.log(`${yAxisTitle}:`, data[yAxisKey]);
    console.log('Zone/Quadrant:', data[quadrantKey]);
    console.log('Full Data:', data);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;

      // Use custom tooltip text if available
      if (dataPoint?.[tooltipKey]) {
        const tooltipText = typeof dataPoint[tooltipKey] === 'object'
          ? JSON.stringify(dataPoint[tooltipKey])
          : String(dataPoint[tooltipKey]);
        return (
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '8px 12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              maxWidth: '250px'
            }}
          >
            <Typography sx={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
              {tooltipText}
            </Typography>
          </Box>
        );
      }

      // Default tooltip
      const xValue = dataPoint?.[xAxisKey];
      const yValue = dataPoint?.[yAxisKey];

      // Ensure values are strings/numbers, not objects
      const displayXValue = typeof xValue === 'object' ? JSON.stringify(xValue) : xValue;
      const displayYValue = typeof yValue === 'object' ? JSON.stringify(yValue) : yValue;

      return (
        <Box
          sx={{
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '8px 12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
            {xAxisTitle}: {displayXValue}{xAxisUnit}
          </Typography>
          <Typography sx={{ fontSize: '12px' }}>
            {yAxisTitle}: {displayYValue}{yAxisUnit}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      <ResponsiveContainer width="100%" height={graphHeight}>
        <RechartsScatterChart
          margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            type="number"
            dataKey={xAxisKey}
            name={xAxisTitle}
            unit={xAxisUnit}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={{ value: xAxisTitle, position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } }}
          />
          <YAxis
            type="number"
            dataKey={yAxisKey}
            name={yAxisTitle}
            unit={yAxisUnit}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 20, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />

          {/* Reference lines for zones */}
          {showZones && referenceLines && referenceLines.map((line, idx) => (
            line.axis === 'y' ? (
              <ReferenceLine
                key={`ref-y-${idx}`}
                y={line.value}
                stroke={line.color || '#9CA3AF'}
                strokeDasharray="5 5"
                strokeWidth={1.5}
                label={line.label ? { value: line.label, position: 'right', fontSize: 10 } : undefined}
              />
            ) : (
              <ReferenceLine
                key={`ref-x-${idx}`}
                x={line.value}
                stroke={line.color || '#9CA3AF'}
                strokeDasharray="5 5"
                strokeWidth={1.5}
                label={line.label ? { value: line.label, position: 'top', fontSize: 10 } : undefined}
              />
            )
          ))}

          {/* Default reference lines for backward compatibility */}
          {showZones && !referenceLines && (
            <>
              <ReferenceLine y={idealZoneThreshold.minQuality} stroke="#10B981" strokeDasharray="5 5" strokeWidth={1.5} />
              <ReferenceLine y={problemZoneThreshold.maxQuality} stroke="#EF4444" strokeDasharray="5 5" strokeWidth={1.5} />
            </>
          )}

          {/* Single Scatter with all data points colored by quadrant */}
          <Scatter
            name="Points"
            data={data}
            shape="circle"
            onClick={handlePointClick}
            cursor="pointer"
          >
            {data.map((entry, index) => {
              const quadrantName = entry?.zone?.toLowerCase();
              const quadrant = effectiveQuadrants.find((q: QuadrantConfig) => q.name === quadrantName);
              const fillColor = entry?.color || quadrant?.color || '#6B7280'; // default color
              return <Cell key={`cell-${index}`} fill={fillColor} />;
            })}
          </Scatter>
        </RechartsScatterChart>
      </ResponsiveContainer>

      {/* Quadrant Labels */}
      {showZones && effectiveQuadrants.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          {effectiveQuadrants.filter((q: QuadrantConfig) => (quadrantCounts[q.name] || q.count || 0) > 0).map((quadrant: QuadrantConfig, idx: number) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: quadrant.color }} />
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#6B7280' }}>
                {quadrantCounts[quadrant.name] || quadrant.count || 0} in {quadrant.label}
                {quadrant.percentage ? ` (${quadrant.percentage.toFixed(1)}%)` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={details?.subPoints}
        colors={['#000000']}
      />
    </ChartCardWrapper>
  );
};

export default ScatterChart;
